import React, { useState, useMemo, useRef, useCallback } from 'react'
import { Search } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'

export const Selector = ({ datas, labelName, value, update, showCode, isSearchable = false, renderCustomInput }) => {
  const [searchText, setSearchText] = useState('')
  const searchInputRef = useRef(null)

  // Filter items based on search text (only when searchable)
  const filteredData = useMemo(() => {
    if (!isSearchable || !searchText) return datas

    return datas.filter((item) => {
      const searchableText = `${showCode && item.code ? `${item.code} ` : ''}${item.name}`.toLowerCase()
      return searchableText.includes(searchText.toLowerCase())
    })
  }, [datas, searchText, showCode, isSearchable])

  const handleSearchChange = useCallback((e) => {
    setSearchText(e.target.value)
  }, [])

  const handleValueChange = useCallback((newValue) => {
    update(newValue)
    setSearchText('') // Clear search after selection
  }, [update])

  // Prevent the dropdown from closing when interacting with search
  const handleSearchPointerDown = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleSearchClick = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    // Manually focus the input
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [])

  const handleSearchKeyDown = useCallback((e) => {
    e.stopPropagation()
    // Allow escape to close
    if (e.key === 'Escape' && searchInputRef.current) {
      searchInputRef.current.blur()
    }
  }, [])

  // For non-searchable version, use the simple Select without controlled open state
  if (!isSearchable && !renderCustomInput) {
    return (
      <Select onValueChange={update} defaultValue={value} value={value}>
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder={`Select a ${labelName}`} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>Select {labelName}</SelectLabel>
            {datas.map((item, index) => (
              <SelectItem
                key={`${item.code || item.value || 'item'}-${index}`}
                value={item.code || item.value}
              >
                {showCode && `${item.code} - `}
                {item.name || item.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    )
  }

  // For searchable or custom input version
  return (
    <Select
      onValueChange={handleValueChange}
      defaultValue={value}
      value={value}
    >
      <SelectTrigger className="w-[180px]">
        {renderCustomInput ? (
          <span>{value === '' ? `Select a ${labelName}` : value}</span>
        ) : (
          <SelectValue placeholder={`${value || `Select a ${labelName}`}`} />
        )}
      </SelectTrigger>
      <SelectContent>
        {renderCustomInput}
        {/* Search input container with pointer event handling */}
        {isSearchable && (
          <div
            className="flex items-center border-b px-3 pb-2"
            onPointerDown={handleSearchPointerDown}
            onPointerMove={handleSearchPointerDown}
            onClick={handleSearchClick}
          >
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              ref={searchInputRef}
              placeholder={`Search ${labelName}...`}
              value={searchText}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              onPointerDown={handleSearchPointerDown}
              onClick={handleSearchClick}
              className="h-8 w-full border-0 bg-transparent p-0 text-sm outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
              autoFocus
            />
          </div>
        )}

        <SelectGroup>
          <SelectLabel>Select {labelName}</SelectLabel>
          {filteredData.length > 0 ? (
            filteredData.map((item, index) => (
              <SelectItem
                key={`${item.code || item.value || 'filtered'}-${index}`}
                value={item.code || item.value}
              >
                {showCode && `${item.code} - `}
                {item.name || item.label}
              </SelectItem>
            ))
          ) : isSearchable && searchText ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              No {labelName} found
            </div>
          ) : null}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

export default Selector
